import { useCustomToast } from '@/hooks/use-custom-toast';
import { useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router';
import {
  connectAppStoreChannelValidationSchema,
  TConnectAppStoreChannelValidation,
  useConnectAppStoreChannel,
} from '../../../api/connect-app-store-channel.api';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_APP_STORE_CHANNEL_QUERY_KEY } from '../../../api/get-list-app-store-channel.api';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/_ui/spinner';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { TAppStore } from '../../../types/apps-store.type';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';

export type TSelectConnectAppStoreChannelModalType = 'SelectConnectAppStoreChannelModal';
export interface ISelectConnectAppStoreChannelModalProps {
  listAppStore: TAppStore[];
}

const SelectConnectAppStoreChannelModal = memo(({ listAppStore }: ISelectConnectAppStoreChannelModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenSelectConnectAppStoreChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('SelectConnectAppStoreChannelModal');
  }, [channelModalRootActions]);

  const form = useForm<TConnectAppStoreChannelValidation>({
    mode: 'onChange',
    resolver: zodResolver(connectAppStoreChannelValidationSchema),
  });

  const mutateConnectAppStoreChannel = useConnectAppStoreChannel({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Failed to connect app store channel';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_APP_STORE_CHANNEL_QUERY_KEY],
          }),
        ]);
        channelModalRootActions.closeModal('SelectConnectAppStoreChannelModal');
        navigate('/channel-integrations/app-store');
        showToast({ message: 'App store channel connected successfully', type: 'success' });
      },
    },
  });

  // HELPERS
  const selectedApps = form.watch('apps');

  const isDisabledButton = useMemo(() => {
    return mutateConnectAppStoreChannel.isPending || selectedApps?.length === 0;
  }, [mutateConnectAppStoreChannel.isPending, selectedApps]);

  // HANDLERS
  const handleSubmitForm = useCallback(() => {
    if (selectedApps?.length === 0) {
      showToast({
        message: 'Please select at least one app to connect!',
        type: 'error',
      });
      return;
    }
    mutateConnectAppStoreChannel.mutate({ apps: selectedApps });
  }, [mutateConnectAppStoreChannel, selectedApps, showToast]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('SelectConnectAppStoreChannelModal');
    form.reset();
  }, [channelModalRootActions, form]);

  return (
    <Modal
      isOpen={isOpenSelectConnectAppStoreChannelModal}
      onClose={handleClose}
      isCloseOutside={false}
      className="xl:w-[500px] w-[90vw] p-[32px]"
      lockBodyScroll
    >
      <ModalHeader>
        <ModalTitle>Select App To Connect</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form id="select-connect-app-store-channel-form" className="gap-[6px] max-h-[280px] flex flex-col">
          <span className="text-[14px] font-normal text-[#25151E]">Total App: {listAppStore?.length || 0}</span>

          <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-[#D9D9D9] scrollbar-track-transparent pr-[12px]">
            {listAppStore?.map((item: TAppStore) => (
              <Controller
                key={item.id}
                control={form.control}
                name="apps"
                defaultValue={[]} // ✅ ensures apps array is initialized
                render={({ field }) => {
                  const value = field.value || [];
                  const isChecked = value.some((v: TAppStore) => v.id === item.id);

                  const toggle = () => {
                    if (isChecked) {
                      field.onChange(value.filter((v: TAppStore) => v.id !== item.id));
                    } else {
                      field.onChange([...value, item]);
                    }
                  };

                  return (
                    <button
                      type="button"
                      onClick={toggle}
                      className={cn(
                        'cursor-pointer w-full flex items-center gap-[16px] h-max rounded-[8px] border border-[#D9D9D9] px-[12px] py-[8px] shrink-0 mb-2',
                        isChecked && 'border-[#0C8DBF] bg-[#0C8DBF]/10'
                      )}
                    >
                      <label className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          className={cn(
                            'peer h-4 w-4 appearance-none border border-[#D9D9D9] rounded-sm shrink-0 shadow-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0C8DBF]',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            isChecked && 'bg-[#0C8DBF] border-[#0C8DBF] text-white'
                          )}
                        />
                        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          {isChecked && (
                            <svg
                              className="h-3 w-3 text-white"
                              fill="none"
                              viewBox="0 0 16 16"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5l3 3L13 5.5" />
                            </svg>
                          )}
                        </div>
                      </label>

                      <div>
                        <p className={cn('flex flex-col font-normal', isChecked && 'text-[#0C8DBF]')}>
                          <span className="text-[14px] font-normal text-[#25151E]">{item?.appName}</span>
                          <span className="text-[12px] font-normal text-[#9D9D9C]">{item?.id}</span>
                        </p>
                      </div>
                    </button>
                  );
                }}
              />
            ))}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          type="button"
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleSubmitForm();
          }}
          disabled={isDisabledButton}
        >
          {mutateConnectAppStoreChannel.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Connect
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
SelectConnectAppStoreChannelModal.displayName = 'SelectConnectAppStoreChannelModal';
export default SelectConnectAppStoreChannelModal;
